import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { post } from '../shared/apiClient';
import { getQueryParam } from '../shared/queryParams';

type State = 'verifying' | 'success' | 'error';

function verifyCacheKey(email: string, token: string): string {
  return `verify-email:${email}:${token}`;
}

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const [state, setState] = useState<State>('verifying');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const email = getQueryParam(location.search, 'email');
  const token = getQueryParam(location.search, 'token');

  useEffect(() => {
    if (!token || !email) {
      setErrorMsg(t('verify_email.invalid_link'));
      setState('error');
      return;
    }

    const cacheKey = verifyCacheKey(email, token);
    if (sessionStorage.getItem(cacheKey) === 'ok') {
      setState('success');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await post('/auth/verify-email', { email, otp: token });
        if (cancelled) return;
        sessionStorage.setItem(cacheKey, 'ok');
        setState('success');
      } catch (err: unknown) {
        if (cancelled) return;
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : t('verify_email.expired');
        setErrorMsg(msg);
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, token, t]);

  const signInUrl = `/signin${email ? `?email=${encodeURIComponent(email)}` : ''}`;

  return (
    <AuthPageShell>
      <div className="flex w-full max-w-[608px] flex-col gap-8 rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] p-6">
        {state === 'verifying' ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#6ECFCF] border-t-transparent" />
            <p className="text-sm text-gray-600">{t('verify_email.verifying')}</p>
          </div>
        ) : state === 'success' ? (
          <>
            <div className="flex items-center justify-between">
              <Link to={signInUrl} className="text-gray-500 hover:text-gray-700">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <h1 className="flex-1 text-center text-[22px] font-semibold leading-tight text-[#131315]">
                {t('verify_email.success_title')}
              </h1>
            </div>

            <div className="flex justify-center">
              <Link
                to={signInUrl}
                className="flex w-full max-w-[400px] items-center justify-center rounded-lg border border-[#6ECFCF] bg-[#6ECFCF] px-4 py-2 text-[13px] font-medium text-[#131315] transition-colors hover:brightness-95"
              >
                {t('verify_email.sign_in')}
              </Link>
            </div>

            <p className="text-center text-base text-[#717273]">
              {t('register.step_footer', { step: 4 })}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
              {t('verify_email.failed_title')}
            </h1>
            <p className="text-center text-sm text-red-600" role="alert">
              {errorMsg}
            </p>
            <div className="flex justify-center">
              <Link to="/signin" className="text-sm text-gray-600 hover:underline">
                {t('verify_email.back_to_sign_in')}
              </Link>
            </div>
          </>
        )}
      </div>
    </AuthPageShell>
  );
};

export default VerifyEmailPage;
